import type { Plate } from "../shared/plates";
import type { PackId, PatronView } from "../shared/patron";
import type { FeedbackRow } from "./admin";
import type { Compared } from "../shared/seeds";
import type { Overview } from "../shared/overview";
import { SOURCE_RE } from "../shared/referrals";
import { restingNote } from "../shared/resting";
import type { Cut, DrawAsChoice, LookView, Resource, StoryIndex, StoryView, StreamLine } from "../shared/story";

export class ApiError extends Error {
  constructor(readonly status: number, readonly code: string, readonly body: Record<string, unknown>) {
    super(code);
  }
}

async function call<T>(method: string, path: string, body?: unknown, extra: Record<string, string> = {}): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: "same-origin",
    headers: { ...(body === undefined ? {} : { "content-type": "application/json" }), ...extra },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) throw new ApiError(res.status, String(data.error ?? (res.status >= 500 ? "internal" : "error")), data);
  return data as T;
}

/** Posts and reads the NDJSON reply line by line. Aborting `signal` is how the player cuts in. */
async function stream(path: string, body: unknown, onLine: (l: StreamLine) => void, signal: AbortSignal, headers: Record<string, string> = {}): Promise<void> {
  const res = await fetch(path, {
    method: "POST", credentials: "same-origin", signal,
    headers: { "content-type": "application/json", ...headers }, body: JSON.stringify(body),
  });
  if (!res.ok || !res.body) {
    const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    throw new ApiError(res.status, String(data.error ?? "error"), data);
  }
  const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
  let buf = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += value;
    let nl: number;
    while ((nl = buf.indexOf("\n")) >= 0) {
      const raw = buf.slice(0, nl).trim();
      buf = buf.slice(nl + 1);
      if (raw) onLine(JSON.parse(raw) as StreamLine);
    }
  }
}

/** Where this visit came from, as the front page passed it on (/play?from=…): sent with a first story, never stored. */
const FROM = (() => { try { const f = new URLSearchParams(location.search).get("from"); return f && SOURCE_RE.test(f) ? f : undefined; } catch { return undefined; } })();

/** The browser's time zone: the nightly dream runs in the player's night. */
const tz = () => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return undefined; } };

export const api = {
  stories: () => call<StoryIndex>("GET", "/api/stories"),
  story: (id: string) => call<{ story: StoryView; resources: Resource[] | null }>("GET", `/api/stories/${id}`),
  begin: (opening: string, onLine: (l: StreamLine) => void, signal: AbortSignal, turnstile?: string, scene?: { truths: string[]; fear: string; seed?: string }) =>
    stream("/api/stories", {
      opening, tz: tz(), ...(scene?.seed ? { seed: scene.seed } : {}), ...(FROM ? { from: FROM } : {}),
      ...(scene?.truths.some((t) => t.trim()) ? { truths: scene.truths.map((t) => t.trim()).filter(Boolean).slice(0, 3) } : {}),
      ...(scene?.fear.trim() ? { fear: scene.fear.trim() } : {}),
    }, onLine, signal, turnstile ? { "cf-turnstile-response": turnstile } : {}),
  /** Pass it on (co-creation step 3): the opening as it would go, or its link; others' endings from the same opening. */
  seed: (id: string) => call<{ code: string | null; opening: string; truths: string[]; compare: Compared[] }>("GET", `/api/stories/${id}/seed`),
  passOn: (id: string, opening: string, truths: string[]) => call<{ code: string }>("POST", `/api/stories/${id}/seed`, { opening, truths }),
  seedOf: (code: string) => call<{ seed: { code: string; opening: string; truths: string[]; voice: string; mine: boolean } }>("GET", `/api/seeds/${code}`),
  reportSeed: (code: string) => call<{ ok: true }>("POST", `/api/seeds/${code}/report`, {}),
  turn: (id: string, input: string, cut: Cut | null, onLine: (l: StreamLine) => void, signal: AbortSignal, pull?: string) =>
    stream(`/api/stories/${id}/turn`, { input, tz: tz(), ...(cut ? { cut } : {}), ...(pull ? { pull } : {}) }, onLine, signal),
  config: () => call<{ dreamNow: boolean; turnstileSiteKey: string | null }>("GET", "/api/config"),
  settings: () => call<{ mature: boolean; chapters: boolean; voice: string; voiceFollows: boolean; drawAs: DrawAsChoice }>("GET", "/api/settings"),
  setDrawAs: (drawAs: DrawAsChoice) => call<{ drawAs: DrawAsChoice }>("PUT", "/api/settings", { drawAs }),
  setVoiceFollows: (voiceFollows: boolean) => call<{ voiceFollows: boolean }>("PUT", "/api/settings", { voiceFollows }),
  setVoicePref: (voice: string) => call<{ mature: boolean; chapters: boolean; voice: string }>("PUT", "/api/settings", { voice }),
  setMature: (mature: boolean, adult = false) => call<{ mature: boolean; chapters: boolean }>("PUT", "/api/settings", { mature, ...(adult ? { adult } : {}) }),
  setChapters: (chapters: boolean) => call<{ mature: boolean; chapters: boolean }>("PUT", "/api/settings", { chapters }),
  account: () => call<{ email: string | null }>("GET", "/api/account"),
  sendCode: (email: string, turnstile?: string) => call<{ ok: true }>("POST", "/api/account/code", { email }, turnstile ? { "cf-turnstile-response": turnstile } : {}),
  verify: (email: string, code: string) => call<{ email: string; moved: number; created: boolean; switched?: boolean }>("POST", "/api/account/verify", { email, code }),
  signOut: () => call<{ ok: true }>("POST", "/api/account/signout"),
  patron: () => call<PatronView>("GET", "/api/patron"),
  checkout: (item: "monthly" | "yearly" | PackId, back?: string) => call<{ url: string }>("POST", "/api/patron/checkout", { item, ...(back ? { back } : {}) }),
  portal: () => call<{ url: string }>("POST", "/api/patron/portal"),
  flow: (id: string, free: boolean) => call<{ story: StoryView }>("PUT", `/api/stories/${id}/flow`, { free }),
  voice: (id: string, voice: string) => call<{ story: StoryView }>("PUT", `/api/stories/${id}/voice`, { voice }),
  letters: () => call<{ on: boolean; signedIn: boolean; available: boolean; test?: boolean }>("GET", "/api/letters"),
  testLetter: () => call<{ result: string }>("POST", "/api/letters/test"),
  setLetters: (on: boolean) => call<{ on: boolean }>("PUT", "/api/letters", { on }),
  purchases: () => call<{ purchases: { id: string; item: string; paid_at: number; amount: number; currency: string; refund: number; turns: number; used: number }[]; withdrawals: { id: string; item: string; paid_at: number; withdrawn_at: number; refund_due: number; refunded: number; currency: string; email?: string }[] }>("GET", "/api/purchases"),
  adminOverview: () => call<Overview>("GET", "/api/admin/overview"),
  adminPasses: () => call<{ passes: { email: string; plan: string; created_at: number; ended_at: number | null; signed_in_at: number | null }[] }>("GET", "/api/admin/passes"),
  setPass: (email: string, end = false) => call<{ ok: true }>("POST", "/api/admin/passes", { email, end }),
  adminSeeds: () => call<{ seeds: { code: string; opening: string; truths: string[]; mature: boolean; reportedAt: number; hidden: boolean }[] }>("GET", "/api/admin/seeds"),
  settleSeed: (code: string, action: "restore" | "delete") => call<{ ok: true }>("POST", `/api/admin/seeds/${code}`, { action }),
  adminRefunds: () => call<{ withdrawals: { id: string; item: string; paid_at: number; withdrawn_at: number; refund_due: number; refunded: number; currency: string; email?: string }[] }>("GET", "/api/admin/refunds"),
  withdraw: (id: string) => call<{ ok: true; refund: number; currency: string; done: boolean }>("POST", `/api/purchases/${id}/withdraw`),
  strike: (id: string, kind: "truth" | "house", text: string) => call<{ story: StoryView }>("POST", `/api/stories/${id}/strike`, { kind, text }),
  /** V2, words as things: a name into the pocket, or out of it. */
  pocket: (id: string, thing: string, put: "in" | "out") => call<{ story: StoryView }>("POST", `/api/stories/${id}/pocket`, { thing, put }),
  adminFeedback: () => call<{ feedback: FeedbackRow[] }>("GET", "/api/admin/feedback"),
  feedback: (text: string, page: string, replyTo?: string) => call<{ ok: boolean; resources?: Resource[] }>("POST", "/api/feedback", { text, page, ...(replyTo ? { replyTo } : {}) }),
  dreamNow: (id: string) => call<{ story: StoryView; plan: string[]; prefetch: string[] }>("POST", `/api/stories/${id}/dream-now`),
  firstDream: (id: string) => call<{ story: StoryView }>("POST", `/api/stories/${id}/first-dream`),
  /** The story has ended: its last plate, drawn now (once per story). */
  lastPlate: (id: string) => call<{ story: StoryView }>("POST", `/api/stories/${id}/last-plate`),
  imageUrl: (id: string, i: number) => `/api/stories/${id}/images/${i}`,
  plates: () => call<{ plates: Plate[] }>("GET", "/api/plates"),
  look: (id: string, thing: string) => call<{ look: LookView; cached: boolean }>("POST", `/api/stories/${id}/look`, { thing }),
  finale: (id: string) => call<{ ok: true; finale: boolean }>("POST", `/api/stories/${id}/finale`),
  okay: (id: string) => call<{ ok: true }>("POST", `/api/stories/${id}/okay`),
  remove: (id: string) => call<{ ok: true }>("DELETE", `/api/stories/${id}`),
  forget: () => call<{ ok: true }>("DELETE", "/api/me"),
  /** The player is leaving mid-reply: record how much they saw. */
  beaconCut: (id: string, cut: Cut) => navigator.sendBeacon(`/api/stories/${id}/cut`, JSON.stringify(cut)),
  exportUrl: "/api/export",
};

export function explain(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.code === "quota") return "That's all the story for today. The loom rests until tomorrow.";
    if (e.code === "rate_limited") return "A little slower: the loom is still threading the last line.";
    if (e.code === "uncharted") return restingNote(e.body);
    if (e.code === "busy") return "The last reply is still being written. Try again in a moment.";
    if (e.code === "turnstile") return "Couldn't confirm you're a person. Reload and try again.";
    if (e.code === "refusal") return "The narrator won't take the story there. Your line is still in the box: try it another way.";
    if (e.code === "bad_model_output" || e.code === "internal") return "The loom snagged on our side. Your story is saved; try again in a moment.";
    if (e.code === "conflict") return "Two things changed the story at once. Try again.";
    if (e.code === "patron_needed") return "That's your 40 free turns. Your story is saved; become a Patron to keep weaving.";
    if (e.code === "network_free_used") return "The free turns from this network are used up for today. Your story is saved; become a Patron to keep weaving, or come back tomorrow.";
    if (e.code === "allocation_used") return "This month's turns are woven. Your story is saved; a pack keeps it going until your turns renew.";
  }
  if (e instanceof TypeError) return "Can't reach the loom. Check your connection; your story is saved.";
  return "Something snagged. Try again.";
}
